import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Form,
  Input,
  Layout,
  Space,
  Typography,
  message,
} from "antd";
import axios from "axios";
import apiClient from "../api_client";
import { API_URL } from "../url_config";
import VersionFooter from "./version_footer";

const { Content } = Layout;
const { Title, Text } = Typography;

function Login() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;

    const checkAuth = async () => {
      try {
        const response = await apiClient.get(`${API_URL}/auth/me`);
        const mePayload = response?.data ?? {};
        if (mePayload.username) {
          localStorage.setItem("username", mePayload.username);
        }
        const userCompany = response?.data?.user_company || "";
        if (userCompany) {
          localStorage.setItem("user_company", userCompany);
          if (userCompany === "cap") {
            navigate("/cap/tetra_bus");
          } else {
            navigate(`/${userCompany}/tetra_bus`);
          }
        }
      } catch (error) {
        localStorage.clear();
        message.error("Session invalide. Veuillez vous reconnecter.");
        setErrorMessage("Session invalide. Veuillez vous reconnecter.");
      }
    };

    checkAuth();
  }, [navigate]);

  const handleFinish = async (values) => {
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const response = await axios.post(`${API_URL}/auth/token`, {
        username: values.username,
        password: values.password,
      });

      const payload = response?.data ?? {};

      if (payload.token) {
        localStorage.setItem("access_token", payload.token);
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("auth");
        if (payload.username) {
          localStorage.setItem("username", payload.username);
        }
      } else {
        message.error("Token manquant dans la réponse");
        return;
      }

      const meResponse = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Token ${payload.token}`,
        },
      });

      const mePayload = meResponse?.data ?? {};
      if (mePayload.username) {
        localStorage.setItem("username", mePayload.username);
      }
      const userCompany = mePayload.user_company || "";

      if (userCompany) {
        localStorage.setItem("user_company", userCompany);
        if (userCompany === "cap") {
          navigate("/cap/tetra_bus");
        } else {
          navigate(`/${userCompany}/tetra_bus`);
        }
      } else {
        message.warning("Entreprise utilisateur manquante");
      }

      message.success("Connexion réussie");
    } catch (error) {
      console.error("Erreur de connexion:", error);
      message.error("Identifiants invalides ou erreur serveur");
      setErrorMessage("Identifiants invalides ou erreur serveur");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, rgba(56, 189, 248, 0.14), transparent 30%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Content
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          padding: "32px 20px",
          position: "relative",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: "auto 10% 10% auto",
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "rgba(59, 130, 246, 0.08)",
              filter: "blur(10px)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: "8% auto auto 8%",
              width: 180,
              height: 180,
              borderRadius: "50%",
              background: "rgba(15, 23, 42, 0.04)",
              filter: "blur(18px)",
              pointerEvents: "none",
            }}
          />
          <Card
            bordered={false}
            style={{
              width: "100%",
              maxWidth: 440,
              borderRadius: 20,
              padding: "6px",
              background: "rgba(255, 255, 255, 0.82)",
              boxShadow:
                "0 24px 60px rgba(15, 23, 42, 0.14), 0 8px 24px rgba(15, 23, 42, 0.08)",
              border: "1px solid rgba(148, 163, 184, 0.28)",
              backdropFilter: "blur(14px)",
            }}
            bodyStyle={{ padding: "28px" }}
          >
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Title level={2} style={{ marginBottom: 0 }}>
                Connexion
              </Title>
              <Text type="secondary" style={{ color: "#0f172a" }}>
                Accédez à votre espace sécurisé.
              </Text>
            </Space>

            <Form
              layout="vertical"
              onFinish={handleFinish}
              style={{ marginTop: 24 }}
              requiredMark={false}
            >
              {errorMessage && (
                <div
                  style={{
                    marginBottom: 16,
                    padding: "10px 12px",
                    borderRadius: 8,
                    background: "#fff1f0",
                    border: "1px solid #ffccc7",
                    color: "#cf1322",
                    fontWeight: 500,
                  }}
                >
                  {errorMessage}
                </div>
              )}
              <Form.Item
                label="Nom d'utilisateur"
                name="username"
                rules={[{ required: true, message: "Saisissez votre nom d'utilisateur" }]}
              >
                <Input
                  size="large"
                  placeholder="Votre nom d'utilisateur"
                  style={{ borderRadius: 12, height: 44 }}
                />
              </Form.Item>

              <Form.Item
                label="Mot de passe"
                name="password"
                rules={[{ required: true, message: "Saisissez votre mot de passe" }]}
              >
                <Input.Password
                  size="large"
                  placeholder="Votre mot de passe"
                  style={{ borderRadius: 12, height: 44 }}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={isSubmitting}
                style={{
                  height: 46,
                  borderRadius: 12,
                  fontWeight: 600,
                  background: "linear-gradient(90deg, #0f172a, #1d4ed8)",
                  boxShadow: "0 10px 24px rgba(29, 78, 216, 0.5)",
                }}
              >
                Se connecter
              </Button>
            </Form>
          </Card>
        </div>
      </Content>
      <VersionFooter />
    </Layout>
  );
}

export default Login;
